import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';

/**
 * Custom hook for PostHog analytics
 * Provides easy access to PostHog instance and common tracking methods
 */
export const useAnalytics = () => {
  const posthog = usePostHog();

  // Track user identification (call this when user logs in)
  const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
    if (posthog) {
      posthog.identify(userId, userProperties);
    }
  };

  // Track custom events
  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (posthog) {
      posthog.capture(eventName, properties);
    }
  };

  // Track page views (automatic, but can be called manually if needed)
  const trackPageView = (pageName?: string) => {
    if (posthog) {
      posthog.capture('$pageview', pageName ? { page: pageName } : {});
    }
  };

  // Track feature usage
  const trackFeature = (featureName: string, properties?: Record<string, any>) => {
    if (posthog) {
      posthog.capture('feature_used', {
        feature: featureName,
        ...properties,
      });
    }
  };

  // Reset user (call this when user logs out)
  const resetUser = () => {
    if (posthog) {
      posthog.reset();
    }
  };

  // Get feature flags
  const getFeatureFlag = (flagKey: string) => {
    return posthog?.getFeatureFlag(flagKey);
  };

  // Check if feature flag is enabled
  const isFeatureEnabled = (flagKey: string) => {
    return posthog?.isFeatureEnabled(flagKey) || false;
  };

  return {
    posthog,
    identifyUser,
    trackEvent,
    trackPageView,
    trackFeature,
    resetUser,
    getFeatureFlag,
    isFeatureEnabled,
  };
};

/**
 * Hook to track page views automatically
 * Use this in your route components
 */
export const usePageTracking = (pageName: string) => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView(pageName);
  }, [pageName, trackPageView]);
};